/**
 * Helper untuk menghitung alokasi Dus Campuran (Mix Box) & Dus Lebihan / Sisa Campuran.
 * Mencegah bug angka negatif (-5 pcs) atau lonjakan angka fiktif (18 pcs padahal sisa cuma 13 pcs).
 * 
 * Aturan Alokasi:
 * 1. Dus 1 (Dus Campuran) diisi hingga penuh sesuai kapasitas (mixBoxCap).
 * 2. Jika sisa gabungan melebihi kapasitas dus (isOverflow), varian akan dipartisi secara proporsional
 *    sehingga Dus 2 (Dus Lebihan) berisi sisa gabungan tanpa melebihi jumlah sisa masing-masing varian.
 */

export function calculateMixBoxAllocation(items, mixBoxCap, preferredLebihanVar) {
  const normalizedItems = (items || []).map(it => {
    const name = it.variant_name || it.name || '';
    const rem = Number(it.remainder !== undefined ? it.remainder : (it.rem !== undefined ? it.rem : it.qty)) || 0;
    return { name, remainder: Math.max(0, rem) };
  }).filter(it => it.remainder > 0);

  const totalPooledRem = normalizedItems.reduce((sum, it) => sum + it.remainder, 0);
  const cap = Math.max(1, Number(mixBoxCap) || 25);
  const isOverflow = totalPooledRem > cap;
  const lebihanQty = isOverflow ? (totalPooledRem - cap) : 0;

  if (!isOverflow) {
    return {
      isOverflow: false,
      mixBoxCap: cap,
      totalPooledRem,
      lebihanQty: 0,
      targetLebihanVar: null,
      box1: {
        total: totalPooledRem,
        isFull: totalPooledRem === cap,
        items: normalizedItems.map(it => ({ name: it.name, qty: it.remainder })),
        breakdownStr: normalizedItems.map(it => `${it.name}: ${it.remainder} pcs`).join(' · ')
      },
      box2: null
    };
  }

  // Overflow case:
  // Tentukan varian utama untuk Dus Lebihan (default: varian terakhir)
  const targetLebihanVar = preferredLebihanVar && normalizedItems.some(it => it.name === preferredLebihanVar)
    ? preferredLebihanVar
    : normalizedItems[normalizedItems.length - 1].name;

  // Dus 2 (Dus Lebihan) memerlukan tepat `lebihanQty` pcs
  let neededForBox2 = lebihanQty;
  const box2Map = {};
  normalizedItems.forEach(it => { box2Map[it.name] = 0; });

  // 1. Masukkan target variant ke Dus 2 terlebih dahulu (maksimal sebanyak sisa miliknya)
  const targetItem = normalizedItems.find(it => it.name === targetLebihanVar);
  if (targetItem && neededForBox2 > 0) {
    const take = Math.min(targetItem.remainder, neededForBox2);
    box2Map[targetLebihanVar] = take;
    neededForBox2 -= take;
  }

  // 2. Jika Dus 2 masih butuh pcs tambahan untuk mencapai lebihanQty,
  //    ambil dari varian lain secara berurutan (reverse order)
  if (neededForBox2 > 0) {
    const others = [...normalizedItems].filter(it => it.name !== targetLebihanVar).reverse();
    for (const it of others) {
      if (neededForBox2 <= 0) break;
      const take = Math.min(it.remainder, neededForBox2);
      box2Map[it.name] = (box2Map[it.name] || 0) + take;
      neededForBox2 -= take;
    }
  }

  // 3. Dus 1 mendapatkan sisanya: total remainder - yang masuk ke Dus 2
  const box1Items = [];
  const box2Items = [];

  normalizedItems.forEach(it => {
    const q2 = box2Map[it.name] || 0;
    const q1 = it.remainder - q2;
    if (q1 > 0) {
      box1Items.push({ name: it.name, qty: q1 });
    }
    if (q2 > 0) {
      box2Items.push({ name: it.name, qty: q2 });
    }
  });

  const box1Total = box1Items.reduce((s, it) => s + it.qty, 0);
  const box2Total = box2Items.reduce((s, it) => s + it.qty, 0);

  return {
    isOverflow: true,
    mixBoxCap: cap,
    totalPooledRem,
    lebihanQty,
    targetLebihanVar,
    box1: {
      total: box1Total,
      isFull: box1Total === cap,
      items: box1Items,
      breakdownStr: box1Items.map(it => `${it.name}: ${it.qty} pcs`).join(' · ')
    },
    box2: {
      total: box2Total,
      items: box2Items,
      breakdownStr: box2Items.map(it => `${it.name}: ${it.qty} pcs`).join(' · ')
    }
  };
}
